import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {
    SERVER_METRICS_DEFAULT_RANGE_KEY,
    SERVER_METRICS_POLLING_INTERVAL,
    ServerMetricsRangeKey,
} from 'features/server/lib/serverMetrics';
import {
    buildServerMetricsSearchParams,
    getInitialServerMetricsParams,
    IServerMetricsRange,
    resolveServerMetricsRange,
} from 'features/server/lib/serverMetricsView';

const getNowSeconds = () => Math.floor(Date.now() / 1000);

export interface IUseServerMetricsRange {
    range: IServerMetricsRange,
    activePreset: ServerMetricsRangeKey | null,
    syncNow: () => void,
    handlePresetClick: (_key: ServerMetricsRangeKey) => void,
    handleCustomRangeChange: (_from: number, _to: number) => void,
}

export const useServerMetricsRange = (): IUseServerMetricsRange => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [initialParams] = useState(() => getInitialServerMetricsParams(searchParams));

    const [activePreset, setActivePreset] = useState<ServerMetricsRangeKey | null>(
        initialParams.preset ?? (initialParams.custom ? null : SERVER_METRICS_DEFAULT_RANGE_KEY),
    );
    const [customRange, setCustomRange] = useState<IServerMetricsRange | null>(
        initialParams.preset ? null : initialParams.custom,
    );

    const [now, setNow] = useState(getNowSeconds);
    useEffect(() => {
        const timer = setInterval(() => {
            setNow(getNowSeconds());
        }, SERVER_METRICS_POLLING_INTERVAL);
        return () => clearInterval(timer);
    }, []);

    const range = useMemo(
        () => resolveServerMetricsRange(activePreset, customRange, now),
        [activePreset, now, customRange],
    );

    const customFrom = activePreset ? null : range.from;
    const customTo = activePreset ? null : range.to;

    useEffect(() => {
        setSearchParams(
            (prev) => buildServerMetricsSearchParams(prev, activePreset, customFrom, customTo),
            {replace: true},
        );
    }, [activePreset, customFrom, customTo, setSearchParams]);

    const syncNow = useCallback(() => {
        setNow(getNowSeconds());
    }, []);

    const handlePresetClick = useCallback((key: ServerMetricsRangeKey) => {
        setNow(getNowSeconds());
        setActivePreset(key);
    }, []);

    const handleCustomRangeChange = useCallback((from: number, to: number) => {
        setActivePreset(null);
        setCustomRange({from, to});
    }, []);

    return {range, activePreset, syncNow, handlePresetClick, handleCustomRangeChange};
};

export default useServerMetricsRange;
